import { Fragment, createContext, render } from "preact";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import {
  addComment,
  deleteComment,
  deleteReaction,
  fetchEntry,
  fetchFeed,
  fetchInteractions,
  fetchSession,
  forgetSession,
  redeemWebInvite,
  setReaction,
} from "./api.js";
import {
  entryPath,
  mediaPath,
  parseRoute,
  tokenFromHash,
} from "./routing.js";
import {
  attachmentSummary,
  entryTypeLabel,
  formatEntryDay,
  mediaTypeLabel,
  recordingName,
} from "./format.js";
import { AudioPlayer } from "./AudioPlayer.jsx";
import { GalleryImage } from "./GalleryImage.jsx";
import { demoEnabled } from "./demoData.js";
import { selectionHaptic } from "./feedback.js";
import { mediaErrorReason } from "./media/failureReason.js";
import { startKeepalive } from "./media/keepalive.js";
import { mediaPlan } from "./media/mediaPlan.js";
import { initMediaMode } from "./media/mode.js";
import { InviteBrowserHandoff, NoServiceWorkerBanner } from "./media/NoServiceWorkerBanner.jsx";
import { createGuestSession } from "./media/session.js";
import { createWorkerClient } from "./media/workerClient.js";
import { createGuestNavigation } from "./navigation.js";
import { createTelemetry } from "./telemetry/client.js";
import {
  browserFamily,
  entryCountBucket,
  latencyBucket,
  mediaErrorClass,
  navType,
  osFamily,
  routeFamily,
  sessionEndReason,
  viewportBucket,
} from "./telemetry/env.js";
import "./styles.css";

const guestNavigation = createGuestNavigation();
const navigate = guestNavigation.navigate;

function reportSessionEnd(error, where) {
  const reason = sessionEndReason(error?.message, error?.status);
  if (reason) telemetry.sessionEnded(reason, where);
  return reason;
}

// Each mounted tile or player reports its outcome once. `extra` may be a promise of extra properties.
function useMediaOutcome(kind, surface) {
  const reported = useRef(false);
  return useCallback((outcome, extra) => {
    if (reported.current) return;
    reported.current = true;
    Promise.resolve(extra)
      .catch(() => undefined)
      .then((props) => telemetry.count("media_viewed", { kind, outcome, media_surface: surface, ...props }));
  }, [kind, surface]);
}

// The reason an unavailable item can't be shown: the browser can't use what it has, or it has nothing.
const unavailableReason = (item) => ({ reason: item?.mediaId ? "unsupported_format" : "other" });

// What every media surface needs to build its plan. binding is the one the SW has acknowledged.
// failureReason(src) asks the SW why that source last failed; noSw is why there is no SW.
const NO_REASON = () => Promise.resolve(null);
const MediaContext = createContext({ mode: "pending", demo: false, binding: null, failureReason: NO_REASON, noSw: null });

function useMediaPlan(item, variant, projectionId) {
  const context = useContext(MediaContext);
  return mediaPlan(item, variant, { ...context, projectionId });
}

const playable = (plan) => plan.kind === "sw" || plan.kind === "url";

// Previews are thumbnail-first, like the base.
function previewVariant(item, demo) {
  return isVideo(item) || item?.hasThumbnail === true || demo ? "thumbnail" : "display";
}

function reportPageLoad(view) {
  const [navigation] = performance.getEntriesByType("navigation");
  telemetry.track("page_load", {
    route: routeFamily(view),
    nav: navType(navigation),
    load_ms: latencyBucket(performance.now()),
    browser: browserFamily(navigator.userAgent),
    os: osFamily(navigator.userAgent, navigator.maxTouchPoints),
    viewport: viewportBucket(window.innerWidth),
  });
}

let firstFeedReported = false;

// Only the first feed result measures the first load.
function reportFirstFeed(result, startedAt, entryCount) {
  if (firstFeedReported) return;
  firstFeedReported = true;
  telemetry.track("feed_loaded", {
    result,
    time_to_feed: latencyBucket(performance.now() - startedAt),
    entries: entryCountBucket(entryCount),
  });
}

function Panel({ title, message, action }) {
  return (
    <section className="panel stack">
      <h1>{title}</h1>
      <p className="muted">{message}</p>
      {action}
    </section>
  );
}

function ChevronIcon({ direction = "back" }) {
  return (
    <svg
      width="24" height="24" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2.5"
      stroke-linecap="round" stroke-linejoin="round"
      aria-hidden="true" focusable="false"
    >
      <path d={direction === "back" ? "M15.5 5l-7 7 7 7" : "M8.5 5l7 7-7 7"} />
    </svg>
  );
}

function PlayBadge({ small = false }) {
  return (
    <span className={small ? "play-badge small" : "play-badge"} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="currentColor" focusable="false">
        <path d="M7 4.5v15l12.5-7.5z" />
      </svg>
    </span>
  );
}

function MicIcon() {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2"
      stroke-linecap="round" stroke-linejoin="round"
      aria-hidden="true" focusable="false"
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  );
}

function Topbar({ canGoBack, overlay, onBack }) {
  return (
    <header className={overlay ? "topbar topbar-overlay" : "topbar"}>
      <button
        className={canGoBack ? "icon-button" : "icon-button hidden"}
        type="button"
        aria-label="Back"
        onClick={onBack}
      >
        <ChevronIcon />
      </button>
      <a
        className="brand"
        href="/"
        aria-label="Kinlo home"
        onClick={(event) => {
          event.preventDefault();
          navigate("/");
        }}
      >
        <img
          className="brand-icon"
          src="/kinlo-logo-96-20260919.png"
          width="32"
          height="32"
          alt=""
          aria-hidden="true"
        />
        Kinlo
      </a>
      <span className="topbar-spacer" aria-hidden="true" />
    </header>
  );
}

function SkeletonFeed() {
  return (
    <section className="feed" aria-label="Loading shared moments" aria-busy="true">
      <div className="hero-copy stack">
        <div className="eyebrow">Private family view</div>
        <h1>Opening shared moments</h1>
      </div>
      {Array.from({ length: 3 }).map((_, index) => (
        <article className="feed-card skeleton" key={index}>
          <div className="media-grid single" />
          <div className="card-body">
            <div className="line short" />
            <div className="line" />
            <div className="line medium" />
          </div>
        </article>
      ))}
    </section>
  );
}

function isPhoto(item) {
  return item?.type === "image" || item?.type === "photo" || !item?.type;
}

function isVideo(item) {
  return item?.type === "video";
}

// Photos and videos share collages, heroes, and thumbnails.
function isVisual(item) {
  return isPhoto(item) || isVideo(item);
}

function isAudio(item) {
  return item?.type === "audio" || item?.type === "voice";
}

function recordingLabel(media, index) {
  const recordings = media.filter(isAudio);
  return recordingName(media[index], recordings.indexOf(media[index]) + 1, recordings.length);
}

function EntryText({ entry, detail = false, onOpenEntry }) {
  const projectionId = entry.projectionId || entry.id;
  const title = (entry.title || "").trim();
  const summary = (entry.summary || "").trim();
  const day = formatEntryDay(entry);
  const href = `${entryPath(projectionId)}${window.location.search}`;
  const openLink = (children) => (
    <a className="entry-link" href={href} onClick={(event) => {
      event.stopPropagation();
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      onOpenEntry(projectionId);
    }}>{children}</a>
  );
  const Heading = detail ? "h1" : "h2";
  return (
    <>
      <div className="kicker">
        <span className="type-label">{entryTypeLabel(entry.entryType)}</span>
        {day ? <time dateTime={entry.day || undefined}>{detail || title || summary ? day : openLink(day)}</time> : null}
      </div>
      {title ? <Heading>{detail ? title : openLink(title)}</Heading> : null}
      {!title && detail ? (
        <h1 className="visually-hidden">{`${entryTypeLabel(entry.entryType)}${day ? ` from ${day}` : ""}`}</h1>
      ) : null}
      {summary ? (
        <p className={`${detail ? "story" : "summary"}${title ? "" : " untitled"}`}>
          {detail || title ? summary : openLink(summary)}
        </p>
      ) : null}
    </>
  );
}

function InlineAttachment({ item, index, name, projectionId, surface = "entry" }) {
  // false, true, or "codec" (this browser cannot decode the video, so retrying is pointless).
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Audio and documents have no thumbnail. Playback must use the full object.
  const plan = useMediaPlan(item, "display", projectionId);
  const posterPlan = useMediaPlan(item, "thumbnail", projectionId);
  const src = playable(plan) ? plan.src : "";
  const label = `${mediaTypeLabel(item?.type)} ${index + 1}`;
  const playerRef = useRef(null);
  const started = useRef(false);
  const playRequestedAt = useRef(null);
  const { failureReason } = useContext(MediaContext);
  const reportOutcome = useMediaOutcome(isVideo(item) ? "video" : isAudio(item) ? "audio" : "document", surface);
  useEffect(() => () => playerRef.current?.pause(), []);
  useEffect(() => {
    if (plan.kind === "unavailable") reportOutcome("unavailable", unavailableReason(item));
  }, [plan.kind, item, reportOutcome]);

  if (!src) return <p className="media-unavailable">{name || label} is {plan.kind === "unavailable" ? "unavailable" : "loading"}.</p>;
  if (isAudio(item)) {
    return <AudioPlayer key={src} src={src} label={name || label} surface={surface} telemetry={telemetry} failureReason={failureReason} />;
  }
  if (item.type !== "video") {
    return (
      <a className="document-link" href={src} target="_blank" rel="noopener noreferrer"
        onClick={(event) => {
          event.stopPropagation();
          telemetry.count("media_viewed", { kind: "document", outcome: "ok", media_surface: surface });
        }}>
        Open document {index + 1} <span aria-hidden="true">↗</span>
      </a>
    );
  }
  const Player = item.type;
  const playbackKey = `${item.mediaId || index}:${attempt}`;
  const reportStall = () => {
    if (started.current && telemetry.allowStall(playbackKey)) {
      telemetry.count("video_playback", { kind: "video", phase: "stall", error_class: "none" });
    }
  };
  return (
    <div className={`inline-attachment ${item.type}`} onClick={(event) => event.stopPropagation()}>
      <Player
        key={attempt}
        ref={playerRef}
        src={src}
        controls
        controlsList="nodownload"
        disableRemotePlayback
        x-webkit-airplay="deny"
        playsInline={item.type === "video" ? true : undefined}
        preload="none"
        aria-label={label}
        poster={playable(posterPlan) ? posterPlan.src : undefined}
        onPlaying={() => {
          if (started.current) return;
          started.current = true;
          const t0 = playRequestedAt.current;
          reportOutcome("ok", t0 === null ? undefined : { latency_bucket: latencyBucket(performance.now() - t0) });
          telemetry.count("video_playback", { kind: "video", phase: "start", error_class: "none" });
        }}
        onWaiting={reportStall}
        onStalled={reportStall}
        onError={(event) => {
          const code = event.currentTarget.error?.code;
          const wasStarted = started.current;
          telemetry.count("video_playback", { kind: "video", phase: "failure", error_class: mediaErrorClass(code) });
          // The worker knows why a read failed. The codec message is for when it recorded nothing:
          // not supported, or a decode failure before anything played.
          const recorded = failureReason(src).catch(() => null);
          reportOutcome("error", recorded.then((reason) => ({ reason: reason ?? mediaErrorReason(code) })));
          recorded.then((reason) => setFailed(reason === null && (code === 4 || (code === 3 && !wasStarted)) ? "codec" : true));
        }}
        onPlay={(event) => {
          if (playRequestedAt.current === null) playRequestedAt.current = performance.now();
          document.querySelectorAll("audio, video").forEach((player) => {
            if (player !== event.currentTarget) player.pause();
          });
        }}
      />
      {failed === "codec" ? (
        <div className="media-unavailable" role="status">
          <p>This video can’t play in this browser. Try Safari on an iPhone, iPad or Mac.</p>
        </div>
      ) : failed ? (
        <div className="media-unavailable" role="status">
          <p>This {item.type} couldn’t play. Try again, or use a browser that supports this recording.</p>
          <button className="text-button" type="button" onClick={() => {
            started.current = false;
            playRequestedAt.current = null;
            setFailed(false);
            setAttempt((value) => value + 1);
          }}>Try again</button>
        </div>
      ) : null}
    </div>
  );
}

function MediaTile({ item, index, entryId, onOpen, lead = false, surface = "feed" }) {
  const [failed, setFailed] = useState(false);
  const { demo, failureReason } = useContext(MediaContext);
  const plan = useMediaPlan(item, previewVariant(item, demo), entryId);
  const src = playable(plan) ? plan.src : "";
  const video = isVideo(item);
  const reportOutcome = useMediaOutcome(video ? "video" : "photo", surface);
  useEffect(() => {
    // A video without a thumbnail is normal; a photo without a source is not.
    if (plan.kind === "unavailable" && !video) reportOutcome("unavailable", unavailableReason(item));
  }, [plan.kind, video, item, reportOutcome]);
  return (
    <button
      className="media-tile"
      type="button"
      aria-label={`Open ${video ? "video" : "photo"} ${index + 1}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(entryId, index);
      }}
    >
      {src && !failed ? (
        <img src={src} alt="" loading="lazy"
          onLoad={() => reportOutcome("ok")}
          onError={() => {
            setFailed(true);
            reportOutcome("error", failureReason(src).catch(() => null).then((reason) => ({ reason: reason ?? "unsupported_format" })));
          }} />
      ) : video ? null : (
        <span className="media-placeholder">Photo</span>
      )}
      {video ? <PlayBadge small={!lead} /> : null}
    </button>
  );
}

function FeedMedia({ entry, onOpenMedia }) {
  const media = entry.media || [];
  const entryId = entry.projectionId || entry.id;
  const visuals = media.map((item, index) => ({ item, index })).filter(({ item }) => isVisual(item));
  if (!visuals.length) return null;
  const layout = visuals.length === 1 ? "single" : visuals.length === 2 ? "pair" : "collage";
  return (
    <div className={`media-grid ${layout}`}>
      {visuals.slice(0, 3).map(({ item, index }, position) => (
        <div className={`media-preview-tile${position === 0 ? " large" : ""}`} key={item.mediaId || index}>
          <MediaTile item={item} index={index} entryId={entryId} onOpen={onOpenMedia}
            lead={position === 0 && layout !== "pair"} />
          {position === 2 && visuals.length > 3 ? (
            <button className="media-more" type="button" aria-label={`Open ${visuals.length - 3} more`}
              onClick={(event) => { event.stopPropagation(); onOpenMedia(entryId, visuals[3].index); }}>
              +{visuals.length - 3}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function DetailAttachments({ entry, onOpenMedia }) {
  const media = entry.media || [];
  const entryId = entry.projectionId || entry.id;
  const items = media.map((item, index) => ({ item, index }));
  const others = items.filter(({ item }) => isVisual(item)).slice(1);
  const recordings = items.filter(({ item }) => isAudio(item));
  const documents = items.filter(({ item }) => !isVisual(item) && !isAudio(item));
  return (
    <>
      {recordings.length ? (
        <div className="audio-stack">
          {recordings.map(({ item, index }) => (
            <InlineAttachment item={item} index={index} name={recordingLabel(media, index)}
              projectionId={entryId} key={item.mediaId || index} />
          ))}
        </div>
      ) : null}
      {others.length ? (
        <div className="detail-thumbs">
          {others.map(({ item, index }) => (
            <MediaTile item={item} index={index} entryId={entryId} onOpen={onOpenMedia} surface="entry"
              key={item.mediaId || index} />
          ))}
        </div>
      ) : null}
      {documents.length ? (
        <div className="attachment-list">
          {documents.map(({ item, index }) => (
            <InlineAttachment item={item} index={index} projectionId={entryId} key={item.mediaId || index} />
          ))}
        </div>
      ) : null}
    </>
  );
}

function DetailHeroMedia({ entry, onOpenMedia }) {
  const [failed, setFailed] = useState(false);
  const { demo, failureReason } = useContext(MediaContext);
  const media = entry.media || [];
  const index = media.findIndex(isVisual);
  const first = media[index];
  const entryId = entry.projectionId || entry.id;
  const plan = useMediaPlan(first, previewVariant(first, demo), entryId);
  const src = playable(plan) ? plan.src : "";
  const reportOutcome = useMediaOutcome(isVideo(first) ? "video" : "photo", "entry");
  useEffect(() => {
    if (plan.kind === "unavailable" && first && !isVideo(first)) reportOutcome("unavailable", unavailableReason(first));
  }, [plan.kind, first, reportOutcome]);
  // Recordings play on the page itself, so the badge counts photos and videos only.
  const visualCount = media.filter(isVisual).length;
  if (!first) return null;
  return (
    <button className="detail-hero" type="button" aria-label="Open shared media gallery"
      onClick={() => onOpenMedia(entryId, index)}>
      {src && !failed ? (
        <img src={src} alt="" loading="eager"
          onLoad={() => reportOutcome("ok")}
          onError={() => {
            setFailed(true);
            reportOutcome("error", failureReason(src).catch(() => null).then((reason) => ({ reason: reason ?? "unsupported_format" })));
          }} />
      ) : isVideo(first) ? null : <span className="media-placeholder">Photo</span>}
      {isVideo(first) ? <PlayBadge /> : null}
      {visualCount > 1 ? <span className="detail-photo-count">1 / {visualCount}</span> : null}
    </button>
  );
}

function FeedCard({ entry, onOpenEntry, onOpenMedia }) {
  const projectionId = entry.projectionId || entry.id;
  const attachments = attachmentSummary(entry.media || []);

  return (
    <article className="feed-card" onClick={() => onOpenEntry(projectionId)}>
      <FeedMedia entry={entry} onOpenMedia={onOpenMedia} />
      <div className="card-body">
        <EntryText entry={entry} onOpenEntry={onOpenEntry} />
        {attachments ? (
          <p className="attachment-count"><MicIcon />{attachments}</p>
        ) : null}
      </div>
    </article>
  );
}

// Link previews and scanners must never spend a one-time invite.
const AUTOMATED_AGENT = /(?:bot|crawler|spider|slurp)[\/\s;)_-]|facebookexternalhit|facebot|whatsapp|bingpreview|skypeuripreview|embedly|iframely|mastodon|google-safety|headlesschrome|lighthouse|phantomjs/i;

function looksAutomated() {
  return navigator.webdriver === true || AUTOMATED_AGENT.test(navigator.userAgent || "");
}

function whenVisible() {
  return new Promise((resolve) => {
    if (document.visibilityState === "visible") {
      resolve();
      return;
    }
    const onChange = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", onChange);
      resolve();
    };
    document.addEventListener("visibilitychange", onChange);
  });
}

function hasSession() {
  return fetchSession().then(() => true, () => false);
}

// The redemption in flight, if any. Reloading the page now would cut it off after the server has used the
// invite up and before the browser has its session cookie, and the guest could not get back in.
let redemptionInFlight = null;

function trackRedemption(work) {
  const running = work();
  const finished = () => {
    if (redemptionInFlight === tracked) redemptionInFlight = null;
  };
  const tracked = running.then(finished, finished);
  redemptionInFlight = tracked;
  return running;
}

const redemptionSettled = () => redemptionInFlight ?? Promise.resolve();

let reloadPending = false;

function InviteView({ inviteId, onRedeemed }) {
  const [status, setStatus] = useState(() => {
    if (!tokenFromHash()) return "checking";
    // Detect this synchronously: waiting for media mode would spend the invite first.
    if (!navigator.serviceWorker) return "unsupported";
    return looksAutomated() ? "ready" : "opening";
  });

  const redeeming = useRef(false);
  const redeem = useCallback(async () => {
    // The old document runs until the reloaded one commits; a redemption started now is cut off after the invite is used up.
    if (reloadPending || redeeming.current) return;
    const token = tokenFromHash();
    if (!token) {
      setStatus("missing-token");
      return;
    }

    redeeming.current = true;
    setStatus("opening");
    await trackRedemption(async () => {
      try {
        await redeemWebInvite(inviteId, token);
        onRedeemed();
      } catch {
        // A link already opened in this browser still has its session.
        if (await hasSession()) {
          onRedeemed();
        } else {
          setStatus("failed");
        }
      }
    });
  }, [inviteId, onRedeemed]);

  useEffect(() => {
    let active = true;
    if (status === "checking") {
      hasSession().then((signedIn) => {
        if (!active) return;
        if (signedIn) onRedeemed();
        else setStatus("missing-token");
      });
    } else if (status === "opening") {
      whenVisible().then(() => {
        if (active) redeem();
      });
    }
    return () => {
      active = false;
    };
    // Opening runs once per invite page; later status changes come from redeem itself.
  }, []);

  if (status === "checking" || status === "opening") {
    return <SkeletonFeed />;
  }

  if (status === "missing-token") {
    return (
      <Panel
        title="This invite cannot be opened"
        message="Ask the person who invited you to send a fresh private link."
      />
    );
  }

  if (status === "failed") {
    return (
      <Panel
        title="This invite is no longer available"
        message="Ask the person who invited you to send a fresh private link."
      />
    );
  }

  if (status === "unsupported") {
    return (
      <Panel
        title="You have a private Kinlo invite"
        message="Photos and videos can’t open inside this app. Copy an unused invite to your web browser to see them."
        action={<InviteBrowserHandoff onContinue={redeem} />}
      />
    );
  }

  return (
    <Panel
      title="You have a private Kinlo invite"
      message="Continue on this device to open the shared family gallery."
      action={
        <button className="primary-button" type="button" onClick={redeem}>
          Open family view
        </button>
      }
    />
  );
}

function DeviceFooter({ onForget }) {
  const [confirming, setConfirming] = useState(false);
  const [forgetting, setForgetting] = useState(false);
  const [error, setError] = useState(false);
  const openRef = useRef(null);
  const cancelRef = useRef(null);
  const reopened = useRef(false);

  // Move focus with the swap so keyboard and screen-reader users stay in place.
  useLayoutEffect(() => {
    if (confirming) {
      cancelRef.current?.focus();
    } else if (reopened.current) {
      openRef.current?.focus();
    }
  }, [confirming]);

  if (confirming) {
    return (
      <section className="forget-confirm" role="group" aria-labelledby="forget-title">
        <h2 id="forget-title">Forget this device?</h2>
        <p className="muted">
          You’ll stop seeing your family’s memories in this browser. To come back, ask them to send you a new link.
        </p>
        {error && (
          <p className="interaction-error" role="alert">
            We couldn’t forget this device. Check your connection and try again.
          </p>
        )}
        <div className="forget-actions">
          <button
            className="primary-button secondary"
            type="button"
            ref={cancelRef}
            disabled={forgetting}
            onClick={() => {
              reopened.current = true;
              setError(false);
              setConfirming(false);
            }}
          >
            Cancel
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={forgetting}
            onClick={async () => {
              setError(false);
              setForgetting(true);
              try {
                await onForget();
              } catch {
                setForgetting(false);
                setError(true);
              }
            }}
          >
            {forgetting ? "Forgetting…" : "Forget device"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <footer className="device-footer">
      <p className="muted">Using someone else’s phone or computer?</p>
      <button className="link-button" type="button" ref={openRef} onClick={() => setConfirming(true)}>
        Forget this device
      </button>
    </footer>
  );
}

function FeedView({ entries, loading, error, onOpenEntry, onOpenMedia, onForget }) {
  if (loading) return <SkeletonFeed />;
  if (error) {
    return (
      <Panel
        title="This private family view is no longer available"
        message="Ask the person who invited you to send a fresh private link."
      />
    );
  }
  if (entries.length === 0) {
    return (
      <section className="feed">
        <Panel
          title="No shared moments yet"
          message="New memories and milestones will appear here when they are shared."
        />
        <DeviceFooter onForget={onForget} />
      </section>
    );
  }

  return (
    <section className="feed">
      <div className="hero-copy stack">
        <div className="eyebrow">Private family view</div>
        <h1>Shared with you</h1>
      </div>
      {entries.map((entry) => (
        <FeedCard
          entry={entry}
          key={entry.projectionId || entry.id}
          onOpenEntry={onOpenEntry}
          onOpenMedia={onOpenMedia}
        />
      ))}
      <DeviceFooter onForget={onForget} />
    </section>
  );
}

const ALLOWED_REACTIONS = ["❤️", "😊", "😂", "😍", "🙌", "👍"];

function formatInteractionTime(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(parsed);
}

function InteractionPanel({ entry }) {
  const projectionId = entry?.projectionId || entry?.id || "";
  const [interactions, setInteractions] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [pendingEmoji, setPendingEmoji] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState("");
  const [commentStatus, setCommentStatus] = useState("");
  const [newCommentIds, setNewCommentIds] = useState(new Set());
  const knownCommentIds = useRef(null);
  const composerRef = useRef(null);

  // The note box grows with the guest's text instead of offering a resize handle.
  useLayoutEffect(() => {
    const field = composerRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, 220)}px`;
  }, [commentText]);

  const loadInteractions = useCallback(async (refresh = false) => {
    if (!projectionId) return;
    if (!refresh) setLoading(true);
    setError(null);
    try {
      const data = await fetchInteractions(projectionId);
      const next = data.interactions || [];
      const ids = new Set(next.filter((item) => item.kind === "comment").map((item) => item.interactionId));
      setNewCommentIds(new Set([...ids].filter((id) => knownCommentIds.current && !knownCommentIds.current.has(id))));
      knownCommentIds.current = ids;
      setInteractions(next);
    } catch (error) {
      reportSessionEnd(error, "interaction");
      setError(refresh
        ? "Your change was saved, but comments couldn’t refresh. Reopen this moment to try again."
        : "Couldn’t load reactions and comments. Reopen this moment to try again.");
    } finally {
      setLoading(false);
    }
  }, [projectionId]);

  useEffect(() => {
    setInteractions([]);
    setCommentText("");
    setCommentStatus("");
    knownCommentIds.current = null;
    loadInteractions();
  }, [loadInteractions]);

  const reactionSummary = useMemo(() => {
    const counts = new Map();
    const mine = new Set();
    for (const item of interactions) {
      if (item.kind !== "reaction" || !item.emoji) continue;
      counts.set(item.emoji, (counts.get(item.emoji) || 0) + 1);
      if (item.isMine) mine.add(item.emoji);
    }
    return { counts, mine };
  }, [interactions]);

  const comments = useMemo(
    () => interactions.filter((item) => item.kind === "comment"),
    [interactions],
  );

  const toggleReaction = useCallback(async (emoji) => {
    if (!projectionId || pendingEmoji) return;
    setPendingEmoji(emoji);
    setError(null);
    const adding = !reactionSummary.mine.has(emoji);
    try {
      if (!adding) {
        await deleteReaction(projectionId, emoji);
      } else {
        await setReaction(projectionId, emoji);
        telemetry.count("interaction", { kind: "reaction", result: "ok" });
        selectionHaptic();
      }
      await loadInteractions(true);
    } catch (error) {
      if (adding) telemetry.count("interaction", { kind: "reaction", result: "error" });
      reportSessionEnd(error, "interaction");
      setError("Couldn’t update your reaction. Please try again.");
    } finally {
      setPendingEmoji("");
    }
  }, [loadInteractions, pendingEmoji, projectionId, reactionSummary]);

  const sendComment = useCallback(async (event) => {
    event.preventDefault();
    const trimmed = commentText.trim();
    if (!trimmed || !projectionId || sending) return;
    setSending(true);
    setError(null);
    setCommentStatus("");
    try {
      await addComment(projectionId, trimmed);
      // A guest may start another note while this request is in flight.
      setCommentText((current) => current === commentText ? "" : current);
      telemetry.count("interaction", { kind: "comment", result: "ok" });
      setCommentStatus("Comment sent");
      await loadInteractions(true);
    } catch (error) {
      telemetry.count("interaction", { kind: "comment", result: "error" });
      reportSessionEnd(error, "interaction");
      setError("Your comment wasn’t sent. Your note is still here; please try again.");
    } finally {
      setSending(false);
    }
  }, [commentText, loadInteractions, projectionId, sending]);

  const removeComment = useCallback(async (comment) => {
    if (!projectionId || !comment?.isMine || !comment.interactionId || deletingCommentId) return;
    setDeletingCommentId(comment.interactionId);
    setError(null);
    try {
      await deleteComment(projectionId, comment.interactionId);
      await loadInteractions(true);
    } catch (error) {
      reportSessionEnd(error, "interaction");
      setError("Couldn’t delete your comment. Please try again.");
    } finally {
      setDeletingCommentId("");
    }
  }, [deletingCommentId, loadInteractions, projectionId]);

  return (
    <article className="interactions-panel stack">
      {loading ? <span className="caption">Loading comments…</span> : null}

      <div className="reaction-row" aria-label="React to this moment">
        {ALLOWED_REACTIONS.map((emoji) => {
          const count = reactionSummary.counts.get(emoji) || 0;
          const active = reactionSummary.mine.has(emoji);
          return (
            <button
              className={`reaction-button${active ? " active" : ""}${pendingEmoji === emoji ? " pending" : ""}`}
              type="button"
              aria-pressed={active}
              aria-label={`${active ? "Remove" : "Add"} ${emoji} reaction`}
              aria-busy={pendingEmoji === emoji}
              disabled={loading || Boolean(pendingEmoji)}
              key={emoji}
              onClick={() => toggleReaction(emoji)}
            >
              <span aria-hidden="true">{emoji}</span>
              <span className="reaction-count" aria-hidden={!count}>{count || ""}</span>
            </button>
          );
        })}
      </div>

      {error ? (
        <p className="interaction-error" role="status">
          {error}
        </p>
      ) : null}

      <div className="comment-list">
        {comments.length === 0 && !loading ? (
          <p className="muted">No comments yet. Add one for the family.</p>
        ) : null}
        {comments.map((comment) => (
          <div
            className={`comment-item${comment.isMine ? " mine" : ""}${newCommentIds.has(comment.interactionId) ? " is-new" : ""}`}
            key={comment.interactionId}
          >
            <div className="comment-avatar" aria-hidden="true">
              {comment.authorPhotoUrl ? (
                <img src={comment.authorPhotoUrl} alt="" loading="lazy" />
              ) : (
                <span>{(comment.authorName || "F").trim().charAt(0) || "F"}</span>
              )}
            </div>
            <div className="comment-bubble">
              <div className="comment-meta">
                <strong>{comment.authorName || "Family guest"}</strong>
                {comment.createdAt ? (
                  <span>{formatInteractionTime(comment.createdAt)}</span>
                ) : null}
              </div>
              <p>{comment.text}</p>
              {comment.isMine ? (
                <button
                  className="comment-delete"
                  type="button"
                  disabled={Boolean(deletingCommentId)}
                  onClick={() => removeComment(comment)}
                >
                  {deletingCommentId === comment.interactionId ? "Deleting…" : "Delete"}
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <form className="comment-composer" onSubmit={sendComment}>
        <label className="composer-label" htmlFor={`comment-${projectionId}`}>Leave a note for the family</label>
        <textarea
          ref={composerRef}
          id={`comment-${projectionId}`}
          aria-label="Leave a note for the family"
          maxLength={280}
          placeholder="Write a little note…"
          rows={2}
          value={commentText}
          onInput={(event) => {
            setCommentText(event.currentTarget.value);
            setCommentStatus("");
          }}
        />
        <div className="composer-actions">
          <span className="caption" role="status">{commentStatus}</span>
          <button
            className="primary-button composer-send"
            type="submit"
            disabled={sending || !commentText.trim()}
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </article>
  );
}

function EntryView({ entry, loading, error, onOpenMedia }) {
  if (loading) {
    return (
      <Panel
        title="Opening moment"
        message="Loading the shared media and story."
      />
    );
  }
  if (error || !entry) {
    return (
      <Panel
        title="This moment is no longer available"
        message="It may have been unshared or your access may have changed."
      />
    );
  }

  return (
    <section className="entry-detail">
      <article className="entry-card">
        <DetailHeroMedia entry={entry} onOpenMedia={onOpenMedia} />
        <div className="detail-card-body stack">
          <div className="story-head stack">
            <EntryText entry={entry} detail />
            {entry.sharedByName ? <p className="caption byline">Shared by {entry.sharedByName}</p> : null}
          </div>
          <DetailAttachments entry={entry} onOpenMedia={onOpenMedia} />
          <InteractionPanel entry={entry} />
        </div>
      </article>
    </section>
  );
}

const SWIPE_HINT_KEY = "kinlo.guest.swipeHintSeen";

function useSwipeHint(enabled) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let seen = true;
    try {
      seen = localStorage.getItem(SWIPE_HINT_KEY) === "1";
      localStorage.setItem(SWIPE_HINT_KEY, "1");
    } catch {
      // Without storage the hint would repeat on every visit, so skip it.
    }
    if (seen) return undefined;
    setVisible(true);
    const timer = window.setTimeout(() => setVisible(false), 2600);
    return () => window.clearTimeout(timer);
  }, [enabled]);
  return [visible, setVisible];
}

// Pinch-zooming a photo hands every gesture back to the browser so the guest can pan.
function useViewportZoomed() {
  const [zoomed, setZoomed] = useState(() => (window.visualViewport?.scale || 1) > 1.01);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const update = () => setZoomed(viewport.scale > 1.01);
    viewport.addEventListener("resize", update);
    return () => viewport.removeEventListener("resize", update);
  }, []);
  return zoomed;
}

function useSwipe(onSwipe, enabled) {
  const start = useRef(null);
  const pointers = useRef(new Set());
  return {
    onPointerDown(event) {
      pointers.current.add(event.pointerId);
      // A second finger means a pinch, never a swipe.
      if (pointers.current.size > 1) {
        start.current = null;
        return;
      }
      if (!enabled || (event.pointerType === "mouse" && event.button !== 0)) return;
      // Native players and seek sliders own their own horizontal drags.
      if (event.target instanceof Element && event.target.closest("audio, video, input, button, a")) return;
      start.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
    },
    onPointerUp(event) {
      pointers.current.delete(event.pointerId);
      const origin = start.current;
      start.current = null;
      if (!enabled || !origin || origin.id !== event.pointerId) return;
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      onSwipe(dx < 0 ? 1 : -1);
    },
    onPointerCancel(event) {
      pointers.current.delete(event.pointerId);
      start.current = null;
    },
  };
}

function GalleryView({ entry, index, loading, onOpenMedia }) {
  const { failureReason, noSw } = useContext(MediaContext);
  const media = entry?.media || [];
  const clampedIndex = Math.max(0, Math.min(index, media.length - 1));
  const entryId = entry?.projectionId || entry?.id;
  const current = media[clampedIndex];
  const display = useMediaPlan(current, "display", entryId);
  const thumb = useMediaPlan(current, "thumbnail", entryId);
  const fullSrc = playable(display) ? display.src : playable(thumb) ? thumb.src : "";
  const fallbackSrc = playable(thumb) ? thumb.src : undefined;
  const unavailableReported = useRef("");
  useEffect(() => {
    // Videos and audio report through InlineAttachment.
    if (!current || !isPhoto(current) || fullSrc || display.kind !== "unavailable") return;
    if (unavailableReported.current === current.mediaId) return;
    unavailableReported.current = current.mediaId;
    telemetry.count("media_viewed", { kind: "photo", outcome: "unavailable", media_surface: "gallery", ...unavailableReason(current) });
  }, [current, fullSrc, display.kind]);
  // On video or audio the hint would cover the player controls, so it waits for a photo.
  const [hintVisible, setHintVisible] = useSwipeHint(!loading && media.length > 1 && isPhoto(current));
  const zoomed = useViewportZoomed();
  const swipe = useSwipe((step) => {
    const next = clampedIndex + step;
    if (next < 0 || next >= media.length) return;
    setHintVisible(false);
    onOpenMedia(entryId, next);
  }, !zoomed);

  if (loading) {
    return (
      <Panel
        title="Opening attachment"
        message="Loading the shared media."
      />
    );
  }

  if (!entry || !current) {
    return (
      <Panel
        title="This attachment is no longer available"
        message="It may have been unshared or your access may have changed."
      />
    );
  }

  const title = (entry.title || "").trim();
  return (
    <section className="gallery">
      {noSw ? <NoServiceWorkerBanner reason={noSw} /> : null}
      <div className={zoomed ? "gallery-stage zoomed" : "gallery-stage"} {...swipe}>
        {!isPhoto(current) ? (
          <InlineAttachment
            item={current}
            index={clampedIndex}
            name={isAudio(current) ? recordingLabel(media, clampedIndex) : undefined}
            projectionId={entryId}
            surface="gallery"
            key={current.mediaId || clampedIndex}
          />
        ) : fullSrc ? (
          <GalleryImage
            alt={title}
            fullSrc={fullSrc}
            fallbackSrc={fallbackSrc}
            telemetry={telemetry}
            failureReason={failureReason}
          />
        ) : (
          <div className="gallery-placeholder">{mediaTypeLabel(current?.type)}</div>
        )}
        {hintVisible ? (
          <p className="swipe-hint" role="status">
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"
              aria-hidden="true" focusable="false"
            >
              <path d="M8 7l-5 5 5 5M16 7l5 5-5 5" />
            </svg>
            Swipe for more
          </p>
        ) : null}
      </div>
      <div className="gallery-navigation">
        <button
          className="gallery-nav previous"
          type="button"
          aria-label="Previous attachment"
          disabled={clampedIndex === 0}
          onClick={() => onOpenMedia(entryId, clampedIndex - 1)}
        >
          <ChevronIcon />
        </button>
        <button
          className="gallery-nav next"
          type="button"
          aria-label="Next attachment"
          disabled={clampedIndex >= media.length - 1}
          onClick={() => onOpenMedia(entryId, clampedIndex + 1)}
        >
          <ChevronIcon direction="forward" />
        </button>
      </div>
      <footer className="gallery-caption">
        <div className="scrubber">
          {media.map((_, mediaIndex) => (
            <span
              className={mediaIndex === clampedIndex ? "scrub active" : "scrub"}
              key={mediaIndex}
            />
          ))}
        </div>
        <div className="gallery-copy">
          <p className="caption">
            {clampedIndex + 1} / {media.length}
          </p>
          {title ? <h1>{title}</h1> : <h1 className="visually-hidden">Shared attachment</h1>}
          {entry.summary ? (
            <p className="gallery-summary">{entry.summary}</p>
          ) : null}
        </div>
      </footer>
    </section>
  );
}

function useRoute() {
  const [route, setRoute] = useState(() => parseRoute());

  useEffect(() => {
    const updateRoute = () => setRoute(parseRoute());
    window.addEventListener("popstate", updateRoute);
    return () => window.removeEventListener("popstate", updateRoute);
  }, []);

  return route;
}

function App() {
  const route = useRoute();
  const [feed, setFeed] = useState({
    entries: [],
    loading: true,
    error: null,
  });
  const [entriesById, setEntriesById] = useState(() => new Map());
  const [loadedEntriesById, setLoadedEntriesById] = useState(() => new Map());
  const [sessionForgotten, setSessionForgotten] = useState(false);
  const [mediaMode, setMediaMode] = useState(() => ({ mode: demoMode ? "demo" : "pending", noSw: null }));
  const [sessionBinding, setSessionBinding] = useState(null);
  const [boundBinding, setBoundBinding] = useState(null);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const lastLoadedEntryIdRef = useRef("");
  // Set by openEntry, so the entry view can tell it was opened from the feed.
  const openedFromFeed = useRef("");

  const currentEntry =
    loadedEntriesById.get(route.entryId) || entriesById.get(route.entryId);

  const canGoBack = route.view === "entry" || route.view === "media";

  const openEntry = useCallback((entryId) => {
    openedFromFeed.current = entryId;
    navigate(entryPath(entryId));
  }, []);

  const openMedia = useCallback((entryId, index) => {
    navigate(mediaPath(entryId, index));
  }, []);

  const goBack = guestNavigation.goBack;

  // → "none" when the response has no binding (media then stays on placeholders).
  const observeBinding = useCallback((next) => {
    if (typeof next !== "string" || !next) return "none";
    const change = guestSession.observe(next); // "changed" runs the reset listeners first
    setSessionBinding(next);
    return change;
  }, []);

  const loadFeed = useCallback(async () => {
    const started = performance.now();
    // Refresh returning guests without replacing their feed with skeletons.
    setFeed((previous) => ({ ...previous, error: null }));
    try {
      const data = await fetchFeed();
      observeBinding(data.binding);
      const entries = data.entries || [];
      reportFirstFeed(entries.length > 0 ? "ok" : "empty", started, entries.length);
      setFeed({ entries, loading: false, error: null });
      setEntriesById(
        new Map(entries.map((entry) => [entry.projectionId || entry.id, entry])),
      );
    } catch (error) {
      const reason = reportSessionEnd(error, "feed");
      reportFirstFeed(reason ? "session_ended" : "error", started, 0);
      setFeed({ entries: [], loading: false, error });
    }
  }, [observeBinding]);

  const loadEntry = useCallback(async (entryId) => {
    const source = openedFromFeed.current === entryId ? "feed" : "direct";
    openedFromFeed.current = "";
    if (!entryId || loadedEntriesById.has(entryId)) return;
    lastLoadedEntryIdRef.current = entryId;
    try {
      const data = await fetchEntry(entryId);
      telemetry.track("entry_opened", { result: "ok", source });
      if (lastLoadedEntryIdRef.current !== entryId) return;
      if (observeBinding(data.binding) === "changed") {
        // The reset cleared the ref and both maps. This response belongs to the new session, so keep it.
        lastLoadedEntryIdRef.current = entryId;
        loadFeed();
      }
      setLoadedEntriesById((previous) => {
        const next = new Map(previous);
        next.set(entryId, data.entry);
        return next;
      });
    } catch (error) {
      const reason = reportSessionEnd(error, "entry");
      telemetry.track("entry_opened", { result: reason ? "session_ended" : "error", source });
      if (lastLoadedEntryIdRef.current !== entryId) return;
      setLoadedEntriesById((previous) => {
        const next = new Map(previous);
        next.set(entryId, null);
        return next;
      });
    }
  }, [loadedEntriesById, loadFeed, observeBinding]);

  useEffect(() => {
    let live = true;
    mediaModePromise.then((result) => {
      if (!live) return;
      if (result === "sw" || result === "demo") setMediaMode({ mode: result, noSw: null });
      else setMediaMode({ mode: "no-sw", noSw: result.noSw });
    });
    return () => { live = false; };
  }, []);

  // The SW must know this page's binding before any media URL is built. A bind with no reply
  // within 2 s still proceeds: the key endpoint checks the URL's binding against the cookie (409).
  useEffect(() => {
    if (mediaMode.mode !== "sw" || !sessionBinding) return undefined;
    let live = true;
    workerClient.bind(sessionBinding).then(() => { if (live) setBoundBinding(sessionBinding); });
    return () => { live = false; };
  }, [mediaMode.mode, sessionBinding]);

  useEffect(() => guestSession.onReset(({ reason, previousBinding }) => {
    if (reason === "forgotten") workerClient.purgeAll();
    else if (previousBinding) workerClient.purge(previousBinding);
    setSessionBinding(null);
    setBoundBinding(null);
    setFeed({ entries: [], loading: true, error: null });
    setEntriesById(new Map());
    setLoadedEntriesById(new Map());
    lastLoadedEntryIdRef.current = "";
    setSessionEpoch((value) => value + 1); // remounts every media element, which cancels its stream
    if (reason === "session-changed" || reason === "other-tab") {
      loadFeed();
    } else if (reason === "session-lost") {
      const error = new Error("web-guest/session-unavailable");
      error.status = 401;
      setFeed({ entries: [], loading: false, error });
      navigate("/", { replace: true });
    }
    // redeemed: redeemed() navigates to "/", and the route effect loads the feed.
    // forgotten: forget() shows the forgotten panel.
    // binding-changed: the load that saw the new binding carries on.
  }), [loadFeed]);

  // Warm the keys for the media the feed shows.
  useEffect(() => {
    if (!boundBinding) return;
    const items = [];
    for (const entry of feed.entries) {
      const projectionId = entry.projectionId || entry.id;
      for (const item of (entry.media || []).filter(isVisual).slice(0, 3)) {
        if (item.mediaId) items.push({ projectionId, mediaId: item.mediaId });
      }
    }
    if (items.length) workerClient.prefetch(boundBinding, items);
  }, [boundBinding, feed.entries]);

  useEffect(() => {
    if (mediaMode.mode !== "sw") return undefined;
    return startKeepalive({ ping: () => workerClient.keepalive() });
  }, [mediaMode.mode]);

  // Once per page load, after the first render.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => reportPageLoad(route.view));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (route.view === "invite") return;
    loadFeed();
  }, [loadFeed, route.view]);

  const routeReady = route.view === "feed"
    ? !feed.loading
    : route.view === "invite" || loadedEntriesById.has(route.entryId);
  useLayoutEffect(() => {
    if (routeReady) guestNavigation.restoreScroll();
  }, [route, routeReady]);

  useEffect(() => {
    if ((route.view === "entry" || route.view === "media") && route.entryId) {
      loadEntry(route.entryId);
    }
  }, [loadEntry, route]);

  useEffect(() => {
    if (route.view !== "media") return undefined;
    const onKeyDown = (event) => {
      // Native players own arrow keys for seeking and volume while focused.
      if (event.target instanceof Element && event.target.closest("audio, video, input, textarea, select")) return;
      if (event.key === "Escape") {
        goBack();
      } else if (event.key === "ArrowLeft" && route.mediaIndex > 0) {
        navigate(mediaPath(route.entryId, route.mediaIndex - 1));
      } else if (event.key === "ArrowRight") {
        const count = (currentEntry?.media || []).length;
        if (route.mediaIndex < count - 1) {
          navigate(mediaPath(route.entryId, route.mediaIndex + 1));
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [currentEntry, route]);

  // Errors propagate, so DeviceFooter can show them and offer a retry.
  const forget = useCallback(async () => {
    try {
      await forgetSession();
    } catch (error) {
      workerClient.purgeAll(); // the keys go even when the server call fails
      throw error;
    }
    guestSession.reset("forgotten");
    telemetry.sessionEnded("forgotten", "feed");
    setSessionForgotten(true);
    navigate("/", { replace: true });
  }, []);

  const redeemed = useCallback(() => {
    guestSession.reset("redeemed");
    setSessionForgotten(false);
    navigate("/", { replace: true });
  }, []);

  const content = useMemo(() => {
    if (sessionForgotten) {
      return (
        <Panel
          title="This device has been forgotten"
          message="Use a fresh private link to open this family view again."
        />
      );
    }

    if (route.view === "invite") {
      return <InviteView inviteId={route.inviteId} onRedeemed={redeemed} />;
    }

    if (route.view === "entry") {
      return (
        <EntryView
          entry={currentEntry}
          loading={!loadedEntriesById.has(route.entryId)}
          error={loadedEntriesById.get(route.entryId) === null}
          onOpenMedia={openMedia}
        />
      );
    }

    if (route.view === "media") {
      return (
        <GalleryView
          entry={currentEntry}
          index={route.mediaIndex}
          loading={!loadedEntriesById.has(route.entryId)}
          onOpenMedia={openMedia}
        />
      );
    }

    return (
      <FeedView
        entries={feed.entries}
        error={feed.error}
        loading={feed.loading}
        onOpenEntry={openEntry}
        onOpenMedia={openMedia}
        onForget={forget}
      />
    );
  }, [
    currentEntry,
    feed,
    forget,
    loadedEntriesById,
    openEntry,
    openMedia,
    redeemed,
    route,
    sessionForgotten,
  ]);

  const mediaContext = useMemo(() => ({
    mode: mediaMode.mode,
    demo: mediaMode.mode === "demo",
    binding: boundBinding,
    failureReason: mediaMode.mode === "sw" ? (src) => workerClient.failureReason(src) : NO_REASON,
    noSw: mediaMode.noSw,
  }), [mediaMode.mode, mediaMode.noSw, boundBinding]);

  // The gallery overlay covers this section, so GalleryView shows its own copy.
  const showNoSwBanner = mediaMode.mode === "no-sw" && !sessionForgotten
    && ((route.view === "feed" && !feed.error) || route.view === "entry");

  return (
    <main className="app-shell">
      <MediaContext.Provider value={mediaContext}>
        <Topbar
          canGoBack={canGoBack}
          overlay={route.view === "media"}
          onBack={goBack}
        />
        <section
          className="content"
          aria-live="polite"
          aria-busy={route.view === "feed" && feed.loading}
        >
          {showNoSwBanner ? <NoServiceWorkerBanner reason={mediaMode.noSw} /> : null}
          <Fragment key={sessionEpoch}>{content}</Fragment>
        </section>
      </MediaContext.Provider>
    </main>
  );
}

// One instance per page load, created before the first render. Demo runs and
// link-preview bots send nothing. It is created here, not at the top, because
// looksAutomated reads AUTOMATED_AGENT, which is declared above this line.
const telemetry = createTelemetry({ enabled: !demoEnabled() && !looksAutomated() });
telemetry.installGlobalHandlers();

// The service worker registers before the first render.
const demoMode = demoEnabled();
const guestSession = createGuestSession();
const workerClient = createWorkerClient({
  container: demoMode ? undefined : navigator.serviceWorker,
  telemetry,
  guestSession,
});
const mediaModePromise = demoMode ? Promise.resolve("demo") : initMediaMode({
  beforeReload: redemptionSettled,
  reload: () => {
    reloadPending = true;
    globalThis.location.reload();
  },
});
// Once per page load: the page that reloads never resolves, and demo resolves to "demo".
mediaModePromise.then((result) => {
  if (result?.noSw) telemetry.track("media_no_service_worker", { reason: result.noSw });
});

render(<App />, document.getElementById("app"));
